import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs
} from '@nextui-org/react';
import { apiFetch } from '../lib/api';
import { applySort } from '../lib/sort';

const R2_BASE = 'https://storage.tikcontrol.live/';

function formatBytes(n) {
  if (!n) return '0';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatTs(v) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function Sonidos() {
  const [view, setView] = useState('r2');
  const [toast, setToast] = useState(null);

  // R2
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [prefix, setPrefix] = useState('public/sounds/');

  const PREFIX_PRESETS = [
    { label: 'public/sounds', value: 'public/sounds/' },
    { label: 'users-data', value: 'users-data/' },
    { label: 'widgets', value: 'widgets/' },
    { label: 'agencies', value: 'agencies/' }
  ];

  // KV cache
  const [cacheRows, setCacheRows] = useState([]);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  const [r2Sort, setR2Sort] = useState({
    column: 'uploaded',
    direction: 'descending'
  });
  const [cacheSort, setCacheSort] = useState({
    column: 'name',
    direction: 'ascending'
  });

  async function loadR2(overridePrefix) {
    setLoading(true);
    setError(null);
    try {
      const usePrefix = overridePrefix ?? prefix;
      const params = new URLSearchParams({ prefix: usePrefix, limit: '1000' });
      if (search.trim()) params.set('search', search.trim());
      const res = await apiFetch(`/api/backoffice/sounds?${params}`);
      setRows(res.rows || []);
      setTruncated(!!res.truncated);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadCache() {
    setCacheLoading(true);
    try {
      const res = await apiFetch('/api/backoffice/sounds/cache');
      setCacheRows(res.rows || []);
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setCacheLoading(false);
    }
  }

  useEffect(() => {
    loadR2();
  }, []);

  useEffect(() => {
    if (view === 'cache' && cacheRows.length === 0 && !cacheLoading) {
      loadCache();
    }
  }, [view]);

  async function purgeCache() {
    if (
      !confirm(
        `¿Purgar toda la caché myinstants:* ? Se borrarán ${cacheRows.length || '?'} entradas y se repoblarán bajo demanda.`
      )
    )
      return;
    setPurging(true);
    try {
      const res = await apiFetch('/api/backoffice/sounds/cache', { method: 'DELETE' });
      setToast({ type: 'ok', msg: `Purgadas ${res.deleted} entradas.` });
      setCacheRows([]);
      loadCache();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setPurging(false);
    }
  }

  const totalSize = useMemo(
    () => rows.reduce((acc, r) => acc + (r.size || 0), 0),
    [rows]
  );

  const sortedRows = useMemo(
    () =>
      applySort(rows, r2Sort, {
        key: (r) => (r.key || '').toLowerCase(),
        size: (r) => r.size || 0,
        uploaded: (r) => {
          const t = r.uploaded ? new Date(r.uploaded).getTime() : 0;
          return isNaN(t) ? 0 : t;
        }
      }),
    [rows, r2Sort]
  );

  const sortedCacheRows = useMemo(
    () =>
      applySort(cacheRows, cacheSort, {
        name: (r) => (r.name || '').toLowerCase(),
        expiration: (r) => r.expiration || 0
      }),
    [cacheRows, cacheSort]
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold">Sonidos</h1>
        <p className="text-sm text-default-500">
          R2 bajo <code>sounds/</code> + caché KV <code>myinstants:*</code>.
        </p>
      </div>

      <Tabs
        selectedKey={view}
        onSelectionChange={(k) => setView(String(k))}
        aria-label="Vista"
      >
        <Tab key="r2" title="R2 (archivos)" />
        <Tab key="cache" title="KV myinstants cache" />
      </Tabs>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      )}
      {toast && (
        <div
          className={`rounded-md border p-3 text-sm ${
            toast.type === 'ok'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-danger-200 bg-danger-50 text-danger-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>
              cerrar
            </button>
          </div>
        </div>
      )}

      {view === 'r2' && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label="Prefijo R2"
              size="sm"
              value={prefix}
              onValueChange={setPrefix}
              className="max-w-xs"
            />
            <Input
              label="Buscar en key"
              size="sm"
              value={search}
              onValueChange={setSearch}
              className="max-w-xs"
              isClearable
            />
            <Button onPress={loadR2} color="primary" size="sm" isLoading={loading}>
              Refrescar
            </Button>
            <div className="ml-auto text-sm text-default-500">
              {rows.length} archivos · {formatBytes(totalSize)}{' '}
              {truncated && (
                <Chip size="sm" color="warning" variant="flat">truncado</Chip>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-default-500">Accesos rápidos:</span>
            {PREFIX_PRESETS.map((p) => (
              <Chip
                key={p.value}
                size="sm"
                variant={prefix === p.value ? 'solid' : 'flat'}
                color={prefix === p.value ? 'primary' : 'default'}
                className="cursor-pointer"
                onClick={() => {
                  setPrefix(p.value);
                  loadR2(p.value);
                }}
              >
                {p.label}
              </Chip>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : (
            <Table
              aria-label="Sonidos R2"
              isStriped
              removeWrapper
              classNames={{ th: 'bg-default-100' }}
              sortDescriptor={r2Sort}
              onSortChange={setR2Sort}
            >
              <TableHeader>
                <TableColumn key="key" allowsSorting>Key</TableColumn>
                <TableColumn key="size" allowsSorting>Tamaño</TableColumn>
                <TableColumn key="uploaded" allowsSorting>Subido</TableColumn>
                <TableColumn align="end">Acciones</TableColumn>
              </TableHeader>
              <TableBody emptyContent="Sin archivos.">
                {sortedRows.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell>
                      <code className="text-xs">{r.key}</code>
                    </TableCell>
                    <TableCell>{formatBytes(r.size)}</TableCell>
                    <TableCell>{formatTs(r.uploaded)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="flat"
                          as="a"
                          href={R2_BASE + r.key}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Abrir
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {view === 'cache' && (
        <>
          <div className="flex items-center gap-3">
            <Button onPress={loadCache} size="sm" isLoading={cacheLoading}>
              Refrescar
            </Button>
            <Button
              onPress={purgeCache}
              color="danger"
              variant="flat"
              size="sm"
              isLoading={purging}
              isDisabled={cacheRows.length === 0}
            >
              Purgar todo
            </Button>
            <span className="ml-auto text-sm text-default-500">
              {cacheRows.length} entradas en caché
            </span>
          </div>

          {cacheLoading ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : (
            <Table
              aria-label="Cache myinstants"
              isStriped
              removeWrapper
              classNames={{ th: 'bg-default-100' }}
              sortDescriptor={cacheSort}
              onSortChange={setCacheSort}
            >
              <TableHeader>
                <TableColumn key="name" allowsSorting>Key</TableColumn>
                <TableColumn key="expiration" allowsSorting>Expira</TableColumn>
              </TableHeader>
              <TableBody emptyContent="Caché vacía.">
                {sortedCacheRows.map((r) => (
                  <TableRow key={r.name}>
                    <TableCell>
                      <code className="text-xs">{r.name}</code>
                    </TableCell>
                    <TableCell>{formatTs(r.expiration ? r.expiration * 1000 : null)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}
